<template>
  <div
    class="question"
    :class="{ 'is-active': isActive, 'has-media': hasMedia }"
    :tabindex="isInteractive ? '-1' : null"
  >
    <div class="question-section" :id="questionTextId">
      <div v-if="!example && !hideHeader" class="question-header">
        <h2 v-if="title" class="question-title">
          {{ title }}
        </h2>
      </div>

      <slot />

      <div class="question-content">
        <QuestionVisualMedia
          v-if="hasMedia"
          :media-data="media"
          :question="question.question"
          @load="$emit('questionHeightChanged')"
        />

        <div
          class="question-text-wrapper"
          :class="{
            'down-size': shouldDownSize(),
            'align-to-top': alignTextToTop,
          }"
        >
          <div class="question-text-content" v-html="question.question"></div>
          <TextToSpeech :text="question.question" />
        </div>
      </div>

      <QuestionAnswerLoader
        :data="data"
        :lock-answer="!isInteractive || (enableAnswerLock && hasAnswer)"
        :is-active="isInteractive && isActive"
        :is-loading="savingAnswer"
        :labelled-by="questionTextId"
        :disable-keyboard="disableKeyboard"
        @answer="onAnswer"
        ref="answerLoader"
      />

      <ErrorMessage v-if="errorMessage">
        <template #header>
          {{ $t('question.answer-not-saved-error') }}
        </template>
        <p>{{ errorMessage }}</p>
      </ErrorMessage>

      <LastSavepointIndicator
        v-if="displaySaveTime && answerDate"
        :last-save-date="answerDate"
        :loading="savingAnswer"
      />

      <QuestionIdentifier :data="data" />
    </div>

    <AnswerFeedback
      :show="showFeedbackOverlay"
      :correct="answeredCorrectly"
      :explanation="data.Question.explanation"
      :allow-focus="isInteractive && isActive"
      ref="answerFeedback"
    >
      <slot name="below"></slot>
    </AnswerFeedback>
  </div>
</template>

<script>
import AnswerFeedback from './AnswerFeedback'
import ErrorMessage from './ui/ErrorMessage'
import LastSavepointIndicator from './LastSavepointIndicator'
import QuestionAnswerLoader from './question-answers/QuestionAnswerLoader'
import QuestionIdentifier from './QuestionIdentifier'
import QuestionVisualMedia from './QuestionVisualMedia'
import TextToSpeech from './accessibility/TextToSpeech'

import { stringToDate } from '@/utils/date'
import { edulabGlobalPromise } from '@/utils/scripts'
import { removeTags } from '@/utils/string'

export default {
  name: 'Question',
  components: {
    ErrorMessage,
    AnswerFeedback,
    LastSavepointIndicator,
    QuestionAnswerLoader,
    QuestionIdentifier,
    QuestionVisualMedia,
    TextToSpeech,
  },
  props: {
    data: {
      type: Object,
      required: true,
      validator(data) {
        return !!(data.Question && data.Question.id && data.Question.question)
      },
    },
    errorMessage: String,
    savingAnswer: {
      type: Boolean,
    },
    enableAnswerLock: {
      type: Boolean,
    },
    hideHeader: { type: Boolean, default: false },
    isActive: { type: Boolean, default: true },
    displaySaveTime: { type: Boolean },
    takeFocus: { type: Boolean, default: false },
    /**
     * Prevents interactivity when the question is presented as an example.
     */
    example: { type: Boolean, default: false },
    /**
     * Forces the question into evaluation mode even though no user answer is present.
     */
    evaluate: { type: Boolean, default: false },
    alignTextToTop: { type: Boolean, default: false },
    disableKeyboard: { type: Boolean, default: false },
  },
  data() {
    return {
      focusTimeout: null,
      forceHideFeedbackOverlay: false,
      hideFeedbackTimeout: null,
      answerFeedbackBlockEnabledInterval: null,
      lastAnswerFeedbackBlockHeight: 0,
      heightResetAttemptsAllowed: 20,
    }
  },
  mounted() {
    if (this.isActive && this.takeFocus) {
      this.setFocusOnAnswer(true)
    }
  },
  computed: {
    isInteractive() {
      return !(this.example || this.evaluate)
    },
    title() {
      if (this.data.Topic) {
        return this.data.Topic.name
      }

      return ''
    },
    question() {
      return this.data.Question
    },
    questionTextLength() {
      return removeTags(this.question.question).length
    },
    questionTextId() {
      return `question-text-${this.data.Question.id}`
    },
    media() {
      return this.data.Media
    },
    hasMedia() {
      return this.data.Media && this.data.Media.type_id !== null
    },
    userAnswer() {
      return this.data.UserAnswer
    },
    hasAnswer() {
      return !!this.userAnswer && this.userAnswer.id !== 0
    },
    /**
     * @returns {Date}
     */
    answerDate() {
      if (this.hasAnswer && this.userAnswer.updated) {
        return stringToDate(this.userAnswer.updated)
      }

      return null
    },
    canValidateAnswer() {
      return this.data.UserAnswer && this.data.UserAnswer.correct !== undefined
    },
    answeredCorrectly() {
      return this.hasAnswer ? this.data.UserAnswer.correct : false
    },
    showFeedbackOverlay() {
      return (
        this.evaluate ||
        (this.canValidateAnswer && !this.forceHideFeedbackOverlay)
      )
    },
  },
  watch: {
    isActive(isActive) {
      clearTimeout(this.focusTimeout)

      if (isActive && this.takeFocus) {
        this.focus()
      }
    },
  },
  methods: {
    focus() {
      this.setFocusOnAnswer()
    },
    blur() {
      this.removeFocusOnAnswer()
    },
    shouldDownSize() {
      return this.questionTextLength > 200
    },
    onAnswer() {
      this.$emit('answer', ...arguments)
    },
    /**
     * @param {boolean=} noDelay
     * @public
     */
    setFocusOnAnswer(noDelay) {
      let focusFunction = () => {
        if (!this._isDestroyed) {
          this.$refs.answerLoader.focus()
        }
      }

      if (noDelay) {
        edulabGlobalPromise.then(() => {
          this.makePageReadyAndCallFunction(focusFunction)
        })
      } else {
        // Delay focus 500 ms to make sure the scroll animation has finished before stealing focus.
        // If focus is set before scrolling, the browser does a hard scroll jump instantly, destroying our pretty little animation.
        this.focusTimeout = setTimeout(focusFunction, 500)
      }
    },
    removeFocusOnAnswer() {
      this.$refs.answerLoader.blur()
    },
    makePageReadyAndCallFunction(callback) {
      edulab.base.pageReady()
      callback()
    },
  },
  destroyed() {
    clearTimeout(this.hideFeedbackTimeout)
  },
}
</script>

<style lang="scss" scoped>
// Override Fessor Style
h1,
h2,
h3,
h4 {
  font-family: $headFont;
  font-weight: bold;
}

.question {
  position: relative;
  box-sizing: border-box;
  max-width: 100%;

  .question-section {
    transition: box-shadow 0.2s ease-in-out;
  }

  &:focus,
  &:focus-within {
    outline: 0;

    .question-section,
    ::v-deep .answer-feedback-block {
      box-shadow: $mainBoxShadow;
    }
  }
}

.question-section {
  position: relative;
  padding: 1em 4em 3em;
  margin: 1.5em 0;
  border-radius: var(--theme-border-radius, $mainBorderRadius);
  background-color: $frameBackgroundColor;
}

.question-header {
  margin: -1em -4em 0;
  padding: 0;
  overflow: hidden;
  color: $frameBackgroundColor;
  background-color: var(--theme-main-color);
  text-align: center;
  border-top-left-radius: var(--theme-border-radius, $mainBorderRadius);
  border-top-right-radius: var(--theme-border-radius, $mainBorderRadius);
}

@media (max-width: 30rem) {
  .question-section {
    padding: 1em 2em 3em;
  }
  .question-header {
    margin: -1em -2em 0;
  }
}

.question-title {
  font-size: 1.25em;
  color: inherit;
  margin: 0.75em 0;
  text-transform: uppercase;
  font-weight: bold;
}

.question-text-wrapper {
  position: relative;
  flex: 1;
  margin: 0;
  max-width: 100%;
  font-size: 1.4em;
  font-weight: $fontWeightSemiBold;

  &.align-to-top {
    align-self: flex-start;
  }

  &.down-size {
    font-size: 1.2em;
    font-weight: $fontWeightMedium;
  }
}

.frame-card {
  padding: 1em 4em;

  header {
    margin: -1em -4em;
  }
}

.question-content {
  position: relative;
  display: flex;
  flex-flow: row-reverse wrap;
  align-items: center;
  justify-content: space-between;

  > figure {
    margin: 1em;
    max-width: 20em;
  }

  .text-to-speech {
    margin: -1.2em 0 0 -0.5em;
  }
}

.error-message {
  margin-top: 2em;
}

@media (min-width: 40rem) {
  .question {
    max-width: 40rem;
  }

  .question-content {
    flex-wrap: nowrap;

    .media-wrapper {
      width: 40%;
    }
  }
}
</style>
